import { io, type Socket } from "socket.io-client"
import { api } from "@/lib/api"

let socket: Socket | null = null

// S6 phase 5b (A3): mint a short-lived socket token via the authenticated HTTP
// session. Goes through the `api` instance, so it inherits withCredentials + the
// CSRF header (A1) and is authed by the httpOnly session cookie. Returns null
// on failure so the caller can fail the handshake cleanly and let socket.io
// retry, rather than hanging.
async function mintSocketToken(): Promise<string | null> {
  try {
    const res = await api.post("/auth/socket-token")
    return res.data?.data?.token ?? null
  } catch {
    return null
  }
}

// Returns the singleton socket. Crucially, this does NOT tear down a
// not-yet-connected socket — socket.io-client buffers .emit() calls during
// the handshake and replays them on connect. If we destroyed the socket
// here, listeners attached by an earlier caller (e.g. TestWindow's
// `candidate:join` emit) would be lost when a later getSocket() call
// recreates the socket mid-handshake. That was the actual root cause of
// "candidate shows offline" + "camera black" + "chat broken".
export function getSocket(): Socket {
  if (socket) return socket
  // The socket connects DIRECT to the backend (not through the Vercel /api
  // proxy — WebSockets aren't proxied), so it keeps the absolute VITE_API_URL.
  // It carries no cookie; auth is the minted token in the handshake.
  const socketBase = import.meta.env.VITE_API_URL || "http://localhost:3001"
  socket = io(socketBase, {
    path: "/socket.io",
    // Async auth callback — re-invoked by socket.io on the initial connect AND
    // every reconnect (per the v4 docs), so we mint a FRESH token each time.
    // That's what lets a session outlive the 5-minute token TTL: a reconnect
    // re-mints. On mint failure we still call cb (empty) so the handshake fails
    // cleanly and reconnection retries, instead of hanging.
    auth: (cb) => {
      mintSocketToken().then((token) => cb(token ? { token } : {}))
    },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })
  return socket
}

export function disconnectSocket() {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}
